import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Divider,
  Grid,
  MenuItem,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import { TextField } from "@mui/material";
import axios from "axios";
import { getValidToken } from "../../utils/authUtils";
import * as Yup from "yup";
import { useFirebaseAuth } from "../../hooks/useFirebaseAuth";
import { useState, useEffect } from "react";
import { commonTextFieldProps, smallButtonProps } from "../../utils/styles";
import { OperationType } from "../../types/operationType";
import { handleLogout } from "../../utils/utils";
import { useQueryClient } from "@tanstack/react-query";

// Declare Razorpay global type
declare global {
  interface Window {
    Razorpay: any;
  }
}

interface FormData {
  name: string;
  phoneNumber: string;
  email: string;
  companyName: string;
  message: string;
  numberOfVehicles: number;
  role: string;
  fleetType: string;
  businessType: string;
}
const validationSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Name must be at least 2 characters")
    .required("Name is required"),
  phoneNumber: Yup.string()
    .matches(/^\d{10}$/, "Please enter a valid 10-digit phone number")
    .required("Phone number is required"),
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Email is required"),
  companyName: Yup.string()
    .min(2, "Company name must be at least 2 characters")
    .required("Company name is required"),
  numberOfVehicles: Yup.number()
    .min(1, "Number of vehicles must be at least 1")
    .required("Number of vehicles is required"),
  role: Yup.string()
    .oneOf(["OWNER", "MANAGER", "SUPERVISOR"], "Please select a valid role")
    .required("Role is required"),
  message: Yup.string(),
  fleetType: Yup.string().required("Fleet type is required"),
  businessType: Yup.string().required("Business type is required"),
});

export const NotRegisteredOverlay = () => {
  const { user } = useFirebaseAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  const createAccount = async (values: FormData) => {
    try {
      const token = await getValidToken();
      // Step 1: Create company
      const companyResponse = await axios.post(
        `${process.env.REACT_APP_API_HOST}company`,
        {
          name: values.companyName,
          phoneNumber: values.phoneNumber,
          fleetType: values.fleetType,
          operationType: values.businessType,
          email: values.email,
          fleetSize: values.numberOfVehicles,
          verificationStatus: "PENDING",
          planType: "ACTIVATION",
          planExpiry: null,
          primaryContact: values.name,
          primaryPhone: values.phoneNumber,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!companyResponse.data?.isSuccess || !companyResponse.data?.data?.id) {
        throw new Error(
          companyResponse.data?.message || "Failed to create company"
        );
      }

      const userResponse = await axios.post(
        `${process.env.REACT_APP_API_HOST}auth/signup`,
        {
          name: values.name,
          firebaseUid: user?.uid,
          phoneNumber: values.phoneNumber,
          email: values.email,
          companyId: companyResponse.data.data.id,
          role: values.role,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (userResponse.data?.isSuccess) {
        sessionStorage.setItem("user", JSON.stringify(userResponse.data.data));
        // close the overlay & refresh the page
        window.location.reload();
      } else {
        throw new Error(userResponse.data?.message || "Failed to create user");
      }
    } catch (error: any) {
      throw error;
    }
  };

  const businessTypeBasedOnFleet = (
    fleetType: string
  ): Record<string, string> => {
    if (!fleetType) return {};
    const fleetBusinessTypes: Record<string, Record<string, string>> = {
      TRUCK: {
        "Fixed/MGK Contract": OperationType.CONTRACT,
        "Daily/Weekly/Monthly Booking": OperationType.TOUR_BOOKING,
      },
      BUS: {
        "Daily Service": OperationType.DAILY_BUS,
        "School/Company Contract": OperationType.CONTRACT,
        "Tour Booking": OperationType.TOUR_BOOKING,
      },
      CAR: {
        "Tour Booking": OperationType.TOUR_BOOKING,
        "Fixed Contract": OperationType.CONTRACT,
      },
      CAR_BUS: {
        "Daily Service": OperationType.DAILY_BUS,
        "School/Company Contract": OperationType.CONTRACT,
        "Tour Booking": OperationType.TOUR_BOOKING,
      },
    };
    return fleetBusinessTypes[fleetType] || {};
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        zIndex: 1200,
        display: "flex",
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "center",
        p: { xs: 0.5, sm: 2 },
        overflow: "auto",
      }}
      onContextMenu={handleContextMenu}
    >
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          backgroundColor: "white",
          borderRadius: { xs: 1, sm: 4 },
          m: { xs: 0.25, sm: 1 },
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          width: { xs: "100%", sm: "600px" },
          maxHeight: { xs: "100vh", sm: "100vh" },
          overflow: "auto",
          border: "1px solid #e2e8f0",
        }}
      >
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: { xs: 2, sm: 4 } }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: "primary.main",
              mb: { xs: 0.5, sm: 1 },
              fontSize: { xs: "1.3rem", sm: "2rem" },
            }}
          >
            Welcome to Gaadi Kharcha
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              fontSize: { xs: "0.85rem", sm: "1.1rem" },
              px: { xs: 0.5, sm: 0 },
            }}
          >
            Complete your profile
          </Typography>
        </Box>

        <Divider sx={{ mb: { xs: 2, sm: 2 }, borderColor: "#e2e8f0" }} />

        <Box sx={{ position: "relative" }}>
          {/* Loading Overlay */}
          {isSubmitting ? (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                borderRadius: 2,
                zIndex: 1500,
                backdropFilter: "blur(2px)",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  p: 1,
                  backgroundColor: "white",
                  borderRadius: 2,
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                  border: "1px solid rgba(25, 118, 210, 0.2)",
                }}
              >
                <CircularProgress
                  size={40}
                  sx={{ mb: 2, color: "primary.main" }}
                />
                <Typography
                  variant="body1"
                  color="primary.main"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    textAlign: "center",
                  }}
                >
                  Creating your account...
                </Typography>
              </Box>
            </Box>
          ) : (
            <Formik
              validationSchema={validationSchema}
              initialValues={{
                name: "",
                phoneNumber: user?.phoneNumber?.slice(-10) || "",
                email: "",
                companyName: "",
                numberOfVehicles: 1,
                message: "",
                role: "",
                fleetType: "",
                businessType: "",
                operationType: "",
              }}
              enableReinitialize={true}
              onSubmit={createAccount}
            >
              {({ errors, touched, values, setFieldValue }) => (
                <Form>
                  <fieldset
                    disabled={isSubmitting}
                    style={{ border: "none", padding: 0, margin: 0 }}
                  >
                    <Grid container spacing={{ xs: 1.5, sm: 3 }}>
                      <Grid item xs={12} sm={6}>
                        <Field
                          fullWidth
                          label="Company Name"
                          name="companyName"
                          as={TextField}
                          variant="outlined"
                          required
                          error={
                            touched.companyName && Boolean(errors.companyName)
                          }
                          helperText={touched.companyName && errors.companyName}
                          {...commonTextFieldProps}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          fullWidth
                          label="Your Name"
                          name="name"
                          as={TextField}
                          variant="outlined"
                          required
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name && errors.name}
                          {...commonTextFieldProps}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Field
                          fullWidth
                          label="Phone Number"
                          name="phoneNumber"
                          as={TextField}
                          variant="outlined"
                          required
                          inputProps={{
                            maxLength: 10,
                            pattern: "[0-9]*",
                            inputMode: "numeric",
                          }}
                          error={
                            touched.phoneNumber && Boolean(errors.phoneNumber)
                          }
                          helperText={touched.phoneNumber && errors.phoneNumber}
                          {...commonTextFieldProps}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Field
                          fullWidth
                          label="Email Address"
                          name="email"
                          type="email"
                          as={TextField}
                          variant="outlined"
                          required
                          error={touched.email && Boolean(errors.email)}
                          helperText={touched.email && errors.email}
                          {...commonTextFieldProps}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Field name="role">
                          {({ field, meta }: any) => (
                            <TextField
                              {...field}
                              fullWidth
                              select
                              label="Role"
                              variant="outlined"
                              required
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error}
                              {...commonTextFieldProps}
                            >
                              <MenuItem
                                sx={{ fontSize: "0.75rem" }}
                                value="OWNER"
                              >
                                Owner
                              </MenuItem>
                              <MenuItem
                                sx={{ fontSize: "0.75rem" }}
                                value="MANAGER"
                              >
                                Manager
                              </MenuItem>
                              <MenuItem
                                sx={{ fontSize: "0.75rem" }}
                                value="SUPERVISOR"
                              >
                                Supervisor
                              </MenuItem>
                            </TextField>
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Field name="fleetType">
                          {({ field, meta }: any) => (
                            <TextField
                              {...field}
                              fullWidth
                              select
                              label="Fleet Type"
                              variant="outlined"
                              required
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error}
                              {...commonTextFieldProps}
                            >
                              <MenuItem
                                sx={{ fontSize: "0.75rem" }}
                                value="BUS"
                              >
                                Bus
                              </MenuItem>
                              <MenuItem
                                sx={{ fontSize: "0.75rem" }}
                                value="TRUCK"
                              >
                                Truck
                              </MenuItem>
                              <MenuItem
                                sx={{ fontSize: "0.75rem" }}
                                value="CAR"
                              >
                                Car
                              </MenuItem>
                              <MenuItem
                                sx={{ fontSize: "0.75rem" }}
                                value="CAR_BUS"
                              >
                                Car & Bus
                              </MenuItem>
                            </TextField>
                          )}
                        </Field>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field name="businessType">
                          {({ field, meta }: any) => (
                            <TextField
                              {...field}
                              fullWidth
                              select
                              label="Business Type"
                              variant="outlined"
                              required
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error}
                              {...commonTextFieldProps}
                            >
                              {Object.entries(
                                businessTypeBasedOnFleet(values.fleetType)
                              ).map(([key, value]: [string, string]) => (
                                <MenuItem
                                  key={value}
                                  sx={{ fontSize: "0.75rem" }}
                                  value={value}
                                >
                                  {key}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        </Field>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          fullWidth
                          label="Fleet Size"
                          name="numberOfVehicles"
                          as={TextField}
                          variant="outlined"
                          required
                          type="number"
                          inputProps={{
                            min: 1,
                            step: 1,
                          }}
                          error={
                            touched.numberOfVehicles &&
                            Boolean(errors.numberOfVehicles)
                          }
                          helperText={
                            touched.numberOfVehicles && errors.numberOfVehicles
                          }
                          {...commonTextFieldProps}
                        />
                      </Grid>
                    </Grid>
                  </fieldset>

                  <Box
                    sx={{
                      mt: { xs: 2, sm: 4 },
                      display: "flex",
                      gap: { xs: 2, sm: 2 },
                      flexDirection: { xs: "column-reverse", sm: "row" },
                      justifyContent: { xs: "center", sm: "space-between" },
                      alignItems: { xs: "stretch", sm: "center" },
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => handleLogout(queryClient)}
                      disabled={isSubmitting}
                      {...smallButtonProps}
                    >
                      Logout
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting}
                      {...smallButtonProps}
                    >
                      {isSubmitting ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            justifyContent: "center",
                          }}
                        >
                          <CircularProgress size={18} sx={{ color: "white" }} />
                          <Typography
                            sx={{ fontSize: { xs: "0.8rem", sm: "1rem" } }}
                          >
                            Processing...
                          </Typography>
                        </Box>
                      ) : (
                        "Continue"
                      )}
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          )}
        </Box>
      </Box>
    </Box>
  );
};
